/*
 * product.js — vanilla JS for PDP + product cards.
 *
 * Responsibilities:
 *   - Variant option clicks → swap selected variant, update price / availability / hidden input.
 *   - Gallery: mobile dots, thumbnail clicks, lightbox open/close/next/prev/zoom.
 *   - Quantity +/- on PDP and on product-card stepper.
 *   - Add to cart via AJAX (POST /cart/add.js), dispatch `cart:updated` on document.
 *   - Sticky mobile ATC visibility via IntersectionObserver.
 *   - Product carousel: arrows, dots, swipe.
 *
 * Events dispatched on `document`:
 *   - `cart:updated`  { detail: { source, response } }   after any cart mutation
 *   - `cart:open`     no detail                          when user wants the drawer open
 *   - `cart:error`    { detail: { error } }              on AJAX failure
 *
 * The cart drawer / cart agent should listen for `cart:updated` and refetch /cart.js.
 */

(function () {
  'use strict';

  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const $ = (sel, ctx = document) => ctx.querySelector(sel);

  // ---------- AJAX cart helpers ----------
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data?.description || data?.message || 'Cart request failed');
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  // cart.js listens on window; events dispatched on document never reached it (non-bubbling CustomEvent).
  function dispatchCartUpdated(source, response) {
    window.dispatchEvent(
      new CustomEvent('cart:updated', { detail: { source, response } }),
    );
  }
  function dispatchCartError(error) {
    window.dispatchEvent(new CustomEvent('cart:error', { detail: { error } }));
  }
  function dispatchCartOpen() {
    window.dispatchEvent(new CustomEvent('cart:open'));
  }

  // PDP adds go through cart.js (single owner of /cart/add.js): it syncs the badge, refreshes and opens
  // the drawer and shows the "Added to cart" toast, as Hydrogen does.
  function pdpAdd(variantId, quantity = 1) {
    if (window.BhaatCart?.addToCart) return window.BhaatCart.addToCart(variantId, quantity);
    return cartAdd(variantId, quantity).then((r) => { dispatchCartOpen(); return r; });
  }

  function cartAdd(variantId, quantity = 1) {
    return postJSON('/cart/add.js', {
      items: [{ id: Number(variantId), quantity: Number(quantity) }],
    })
      .then((response) => {
        dispatchCartUpdated('add', response);
        return response;
      })
      .catch((error) => {
        dispatchCartError(error);
        throw error;
      });
  }

  function cartChange(line, quantity) {
    return postJSON('/cart/change.js', { line, quantity })
      .then((response) => {
        dispatchCartUpdated('change', response);
        return response;
      })
      .catch((error) => {
        dispatchCartError(error);
        throw error;
      });
  }

  // ---------- Variant selection (PDP) ----------
  function initVariantSelectors(root) {
    const form = $('[data-product-form]', root);
    if (!form) return;
    const productHandle = form.dataset.productHandle;
    if (!productHandle) return;

    const variantInput = $('[data-product-form-variant-id]', form);
    const atcBtn = $('[data-product-add-to-cart]', form);
    const atcLabel = $('[data-product-atc-label]', form);

    let productData = null;
    fetch(`/products/${productHandle}.js`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((data) => {
        productData = data;
      })
      .catch(() => {});

    function findMatchingVariant() {
      const selected = $$('[data-product-option]', form).map((optEl) => {
        const active = $('[data-product-option-value].is-selected', optEl);
        return active?.dataset.optionValue ?? null;
      });
      if (!productData) return null;
      return productData.variants.find((v) => {
        return v.options.every((val, i) => selected[i] == null || val === selected[i]);
      });
    }

    function syncATC(variant) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        atcBtn.setAttribute('aria-disabled', String(!variant.available));
      }
      if (atcLabel) atcLabel.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';

      const priceEl = root.querySelector('.product-pricing-section .product-price-current');
      if (priceEl && variant.price != null) {
        priceEl.textContent = formatMoney(variant.price);
      }
    }

    form.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-product-option-value]');
      if (!btn) return;
      const optGroup = btn.closest('[data-product-option]');
      $$('[data-product-option-value]', optGroup).forEach((b) =>
        b.classList.remove('is-selected'),
      );
      btn.classList.add('is-selected');
      const variant = findMatchingVariant();
      syncATC(variant);
    });
  }

  // ---------- Quantity stepper (PDP form) ----------
  function initQuantityStepper(root) {
    $$('[data-product-form]', root).forEach((form) => {
      const input = $('[data-quantity-input]', form);
      if (!input) return;
      const dec = $('[data-quantity-decrease]', form);
      const inc = $('[data-quantity-increase]', form);
      const display = $('[data-quantity-display]', form);
      const set = (v) => { input.value = v; if (display) display.textContent = String(v); };
      dec?.addEventListener('click', () => set(Math.max(1, (parseInt(input.value, 10) || 1) - 1)));
      inc?.addEventListener('click', () => set((parseInt(input.value, 10) || 1) + 1));
    });
  }

  // ---------- ATC button states (mirror of Hydrogen's AddToCartButton: Adding… → ✓ Added! for 1600 ms) ----------
  const ATC_SUCCESS_MS = 1600;
  function setAtcState(btn, state) {
    if (!btn) return;
    const label = $('[data-product-atc-label]', btn) || btn;
    if (!btn.dataset.idleLabel) btn.dataset.idleLabel = label.textContent.trim();
    clearTimeout(btn._atcTimer);
    btn.classList.remove('add-to-cart-btn--loading', 'add-to-cart-btn--success');
    if (state === 'loading') {
      btn.classList.add('add-to-cart-btn--loading');
      btn.setAttribute('disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      label.innerHTML = '<span class="add-to-cart-btn-spinner" aria-hidden="true"></span>Adding…';
      return;
    }
    btn.removeAttribute('disabled');
    btn.setAttribute('aria-busy', 'false');
    if (state === 'success') {
      btn.classList.add('add-to-cart-btn--success');
      label.innerHTML = '<span class="add-to-cart-btn-check" aria-hidden="true">✓</span>Added!';
      btn._atcTimer = setTimeout(() => setAtcState(btn, 'idle'), ATC_SUCCESS_MS);
      return;
    }
    label.textContent = btn.dataset.idleLabel;
  }

  // ---------- ATC submit ----------
  function initAddToCart(root) {
    $$('[data-product-form]', root).forEach((form) => {
      const formEl = form.tagName === 'FORM' ? form : $('form', form);
      const formNode = formEl || form;
      formNode.addEventListener('submit', (e) => {
        const variantInput = $('[data-product-form-variant-id]', form);
        const qtyInput = $('[data-quantity-input]', form);
        const atcBtn = $('[data-product-add-to-cart]', form);
        if (!variantInput?.value) return;
        e.preventDefault();
        setAtcState(atcBtn, 'loading');
        pdpAdd(variantInput.value, qtyInput?.value || 1)
          .then(() => setAtcState(atcBtn, 'success'))
          .catch(() => setAtcState(atcBtn, 'idle'));
      });
    });

    // Sticky mobile ATC
    const sticky = $('[data-product-sticky-add]', root);
    if (sticky) {
      sticky.addEventListener('click', () => {
        const variantInput = $('[data-product-form-variant-id]', root);
        if (!variantInput?.value) return;
        sticky.setAttribute('disabled', 'true');
        pdpAdd(variantInput.value, 1)
          .catch(() => {})
          .finally(() => sticky.removeAttribute('disabled'));
      });
    }
  }

  // ---------- Product card cart action ----------
  function initProductCardCartActions(root = document) {
    // The add itself is handled by assets/cart.js (single owner of /cart/add.js). Card state (ADD vs stepper and
    // the stepper quantity) is derived from the cart cart.js publishes on every mutation, like Hydrogen's
    // ProductCardCartAction reading cart lines, so it can never drift from the real cart.
    window.addEventListener('cart:updated', (e) => {
      const items = e.detail?.cart?.items;
      if (!Array.isArray(items)) return;
      const qtyByVariant = {};
      items.forEach((it) => { qtyByVariant[String(it.variant_id)] = (qtyByVariant[String(it.variant_id)] || 0) + Number(it.quantity || 0); });
      $$('[data-product-card-stepper]').forEach((stepper) => {
        const form = stepper.previousElementSibling;
        if (!form || !form.matches('[data-product-card-cart-action]')) return;
        const qty = qtyByVariant[String(stepper.dataset.variantId)] || 0;
        form.hidden = qty > 0;
        stepper.hidden = qty === 0;
        const valEl = $('[data-quantity-input]', stepper);
        if (valEl && qty > 0) valEl.textContent = String(qty);
      });
    });

    root.addEventListener('click', (e) => {
      const incBtn = e.target.closest('[data-product-card-increase]');
      const decBtn = e.target.closest('[data-product-card-decrease]');
      if (!incBtn && !decBtn) return;
      const stepper = (incBtn || decBtn).closest('[data-product-card-stepper]');
      const valEl = $('[data-quantity-input]', stepper);
      const variantId = stepper.dataset.variantId;
      const current = parseInt(valEl?.textContent || '1', 10) || 1;
      const next = incBtn ? current + 1 : current - 1;
      // We don't have a line-id locally; ask cart drawer to handle via /cart/change.js by variant id.
      postJSON('/cart/change.js', { id: variantId, quantity: Math.max(0, next) })
        .then((response) => {
          if (next <= 0) {
            // Restore ADD button
            const form = stepper.previousElementSibling;
            if (form && form.matches('[data-product-card-cart-action]')) {
              stepper.hidden = true;
              form.hidden = false;
            }
          } else if (valEl) {
            valEl.textContent = String(next);
          }
          dispatchCartUpdated('change', response);
          window.BhaatCart?.refresh?.();
        })
        .catch((error) => dispatchCartError(error));
    });
  }

  // ---------- Gallery ----------
  function initGallery(root) {
    const gallery = $('[data-product-gallery]', root);
    if (!gallery) return;

    const mobileTrack = $('[data-gallery-track-mobile]', gallery);
    const dots = $$('[data-gallery-dot]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const mainImg = $('[data-gallery-main-image]', gallery);
    const openBtn = $('[data-gallery-open-lightbox]', gallery);
    const lightbox = $('[data-gallery-lightbox]', gallery);
    const lightboxTrack = $('[data-gallery-lightbox-track]', gallery);
    const lightboxClose = $('[data-gallery-lightbox-close]', gallery);
    const lightboxPrev = $('[data-gallery-lightbox-prev]', gallery);
    const lightboxNext = $('[data-gallery-lightbox-next]', gallery);
    const lightboxCounter = $('[data-gallery-lightbox-counter]', gallery);
    const slides = $$('[data-gallery-lightbox-slide]', gallery);

    let activeIndex = 0;

    function setActive(index) {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === activeIndex));
      // Thumb strips exist in both the mobile and desktop containers (as in Hydrogen): match by data index.
      thumbs.forEach((t) => t.classList.toggle('is-active', Number(t.dataset.galleryIndex) === activeIndex));
      // Swap the desktop main image to the matching lightbox slide image. srcset must be swapped too:
      // the browser ignores a src change while the server-rendered srcset is present.
      const fullImg = slides[activeIndex]?.querySelector('img');
      if (mainImg && fullImg) {
        mainImg.srcset = fullImg.dataset.srcset || '';
        mainImg.src = fullImg.currentSrc || fullImg.src;
        if (fullImg.alt) mainImg.alt = fullImg.alt;
      }
      if (mobileTrack) {
        const slideEl = mobileTrack.querySelector(
          `[data-gallery-index="${activeIndex}"]`,
        );
        slideEl?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }

    dots.forEach((d) => d.addEventListener('click', () => setActive(Number(d.dataset.galleryIndex))));
    thumbs.forEach((t) =>
      t.addEventListener('click', () => setActive(Number(t.dataset.galleryIndex))),
    );

    if (mobileTrack) {
      let rafId = 0;
      mobileTrack.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(() => {
            const w = mobileTrack.clientWidth;
            if (w <= 0) return;
            const idx = Math.round(mobileTrack.scrollLeft / w);
            if (idx !== activeIndex) setActive(idx);
          });
        },
        { passive: true },
      );
    }

    function openLightbox(index = activeIndex) {
      if (!lightbox) return;
      setActive(index);
      scrollLightboxTo(activeIndex);
      if (typeof lightbox.showModal === 'function') lightbox.showModal();
      else lightbox.setAttribute('open', '');
      updateCounter();
    }

    function scrollLightboxTo(index) {
      if (!lightboxTrack) return;
      const w = lightboxTrack.clientWidth;
      lightboxTrack.scrollLeft = index * w;
    }

    function updateCounter() {
      if (lightboxCounter) {
        lightboxCounter.textContent = `${activeIndex + 1} / ${slides.length}`;
      }
    }

    openBtn?.addEventListener('click', () => openLightbox(activeIndex));
    $$('[data-gallery-slide]', gallery).forEach((s) => {
      // mobile slide buttons
    });
    $$('.product-gallery-slide', gallery).forEach((slide, i) => {
      slide.addEventListener('click', () => openLightbox(i));
    });

    lightboxClose?.addEventListener('click', () => lightbox.close());
    lightboxPrev?.addEventListener('click', () => {
      activeIndex = Math.max(0, activeIndex - 1);
      setActive(activeIndex);
      scrollLightboxTo(activeIndex);
      updateCounter();
    });
    lightboxNext?.addEventListener('click', () => {
      activeIndex = Math.min(slides.length - 1, activeIndex + 1);
      setActive(activeIndex);
      scrollLightboxTo(activeIndex);
      updateCounter();
    });

    // Click-to-zoom on lightbox image
    $$('[data-gallery-lightbox-zoom]', gallery).forEach((zoomEl) => {
      zoomEl.addEventListener('click', () => zoomEl.classList.toggle('is-zoomed'));
    });

    // Click backdrop to close
    lightbox?.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest('.product-image-lightbox-zoom') ||
        target.closest('.product-image-lightbox-img') ||
        target.closest('.product-image-lightbox-nav') ||
        target.closest('.product-image-lightbox-close') ||
        target.closest('.product-image-lightbox-counter')
      )
        return;
      lightbox.close();
    });
  }

  // ---------- Sticky ATC visibility ----------
  function initStickyAtc(root) {
    const target = $('[data-add-to-cart-section]', root);
    const sticky = $('[data-product-sticky-atc]', root);
    if (!target || !sticky) return;

    const mq = window.matchMedia('(max-width: 767px)');
    let observer = null;

    function rebind() {
      observer?.disconnect();
      if (!mq.matches) {
        sticky.classList.remove('is-visible');
        sticky.setAttribute('aria-hidden', 'true');
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          const visible = !entry.isIntersecting;
          sticky.classList.toggle('is-visible', visible);
          sticky.setAttribute('aria-hidden', String(!visible));
        },
        { threshold: 0.2 },
      );
      observer.observe(target);
    }

    rebind();
    window.addEventListener('resize', rebind);
    mq.addEventListener?.('change', rebind);
  }

  // ---------- Share button ----------
  function initShare(root) {
    const btn = $('[data-product-share]', root);
    const msg = $('[data-product-share-message]', root);
    if (!btn) return;
    btn.addEventListener('click', async () => {
      try {
        const shareUrl = window.location.href;
        const title = document.title;
        if (navigator.share) {
          await navigator.share({ title, url: shareUrl });
          return;
        }
        await navigator.clipboard.writeText(shareUrl);
        if (msg) {
          msg.textContent = 'Link copied!';
          msg.hidden = false;
          setTimeout(() => {
            msg.hidden = true;
            msg.textContent = '';
          }, 1800);
        }
      } catch {
        if (msg) {
          msg.textContent = 'Unable to share right now';
          msg.hidden = false;
          setTimeout(() => {
            msg.hidden = true;
          }, 1800);
        }
      }
    });
  }

  // ---------- Product carousel ----------
  function initCarousels(root = document) {
    $$('[data-product-carousel]:not([data-scroll-mode])', root).forEach((carousel) => {
      const viewport = $('[data-carousel-viewport]', carousel);
      const track = $('[data-carousel-track]', carousel);
      const dotsContainer = $('[data-carousel-dots]', carousel);
      const prev = $('[data-carousel-prev]', carousel);
      const next = $('[data-carousel-next]', carousel);
      if (!viewport || !track) return;

      const items = Array.from(track.children);
      if (!items.length) return;

      const itemsDesktop = Number(carousel.dataset.itemsDesktop || 3);
      const itemsMobile = Number(carousel.dataset.itemsMobile || 2);
      const loop = carousel.dataset.loop === 'true';

      function itemsPerView() {
        return window.innerWidth >= 640 ? itemsDesktop : itemsMobile;
      }

      function pages() {
        return Math.max(1, Math.ceil(items.length / itemsPerView()));
      }

      let currentPage = 0;

      function renderDots() {
        if (!dotsContainer) return;
        dotsContainer.innerHTML = '';
        const n = pages();
        for (let i = 0; i < n; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel-dot' + (i === currentPage ? ' active' : '');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => goTo(i));
          dotsContainer.appendChild(dot);
        }
      }

      function goTo(page) {
        const max = pages() - 1;
        if (page < 0) page = loop ? max : 0;
        if (page > max) page = loop ? 0 : max;
        currentPage = page;
        const w = viewport.clientWidth;
        viewport.scrollTo({ left: page * w, behavior: 'smooth' });
        renderDots();
      }

      prev?.addEventListener('click', () => goTo(currentPage - 1));
      next?.addEventListener('click', () => goTo(currentPage + 1));
      window.addEventListener('resize', renderDots);

      renderDots();
    });
  }


  // ---------- Product carousel (port of ProductCarousel.jsx: free + paged) ----------
  function initProductCarousels(root = document) {
    $$('[data-product-carousel][data-scroll-mode]', root).forEach((carousel) => {
      const isFree = carousel.dataset.scrollMode === 'free';
      const viewport = $('[data-carousel-viewport]', carousel);
      const track = $('[data-carousel-track]', carousel);
      const dotsEl = $('[data-carousel-dots]', carousel);
      const nav = $('[data-carousel-nav]', carousel);
      const prev = $('[data-carousel-prev]', carousel);
      const next = $('[data-carousel-next]', carousel);
      if (!viewport || !track) return;

      const desktopPer = Number(carousel.dataset.desktopPerView || 3);
      const mobilePer = Number(carousel.dataset.mobilePerView || 2);
      const loop = carousel.dataset.loop !== 'false';
      // Free mode: items live directly in the strip. Paged mode: items live inside .product-carousel-slide pages.
      const items = isFree
        ? Array.from(track.children)
        : $$('.product-carousel-item', track);
      const total = items.length;
      if (!total) return;

      let perView = mobilePer;
      let current = 0;

      function pageCount() {
        return isFree ? Math.max(1, total - perView + 1) : Math.max(1, Math.ceil(total / perView));
      }

      function renderDots() {
        if (!dotsEl) return;
        const n = pageCount();
        dotsEl.innerHTML = '';
        for (let i = 0; i < n; i += 1) {
          const dot = document.createElement('button');
          dot.className = 'carousel-dot' + (i === current ? ' active' : '');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => goTo(i));
          dotsEl.appendChild(dot);
        }
      }

      function markDots() {
        if (!dotsEl) return;
        Array.from(dotsEl.children).forEach((d, i) => d.classList.toggle('active', i === current));
      }

      function step() {
        const first = track.children[0];
        if (!first) return 0;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function rebuildPages() {
        // Paged mode: regroup items into pages of `perView` (the Hydrogen component re-slices on resize).
        const frag = document.createDocumentFragment();
        for (let i = 0; i < total; i += perView) {
          const page = document.createElement('div');
          page.className = 'product-carousel-slide';
          page.style.gridTemplateColumns = `repeat(${perView}, minmax(0, 1fr))`;
          items.slice(i, i + perView).forEach((it) => page.appendChild(it));
          frag.appendChild(page);
        }
        track.innerHTML = '';
        track.appendChild(frag);
      }

      function applyLayout() {
        const nextPer = window.innerWidth >= 640 ? desktopPer : mobilePer;
        const changed = nextPer !== perView;
        perView = nextPer;
        if (isFree) {
          track.style.width = `${Math.max(100, (total / perView) * 100)}%`;
        } else if (changed) {
          rebuildPages();
          current = Math.min(current, pageCount() - 1);
          track.style.transform = `translateX(${-current * 100}%)`;
        }
        if (nav) nav.hidden = total <= perView;
        current = Math.min(current, pageCount() - 1);
        renderDots();
      }

      function goTo(index) {
        const max = pageCount() - 1;
        current = Math.max(0, Math.min(index, max));
        if (isFree) {
          viewport.scrollTo({ left: current * step(), behavior: 'smooth' });
        } else {
          track.style.transform = `translateX(${-current * 100}%)`;
        }
        markDots();
      }

      function go(delta) {
        if (isFree) {
          viewport.scrollBy({ left: delta * step(), behavior: 'smooth' });
          return;
        }
        const max = pageCount() - 1;
        let n = current + delta;
        if (n > max) n = loop ? 0 : max;
        if (n < 0) n = loop ? max : 0;
        goTo(n);
      }

      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));

      if (isFree) {
        viewport.addEventListener('scroll', () => {
          const s = step();
          if (s <= 0) return;
          current = Math.max(0, Math.min(Math.round(viewport.scrollLeft / s), pageCount() - 1));
          markDots();
        }, { passive: true });
      } else {
        let sx = null;
        let sy = null;
        track.addEventListener('touchstart', (e) => {
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
        }, { passive: true });
        track.addEventListener('touchend', (e) => {
          if (sx == null || total <= perView) return;
          const dx = sx - e.changedTouches[0].clientX;
          const dy = sy - e.changedTouches[0].clientY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) go(dx > 0 ? 1 : -1);
          sx = null;
          sy = null;
        }, { passive: true });
      }

      window.addEventListener('resize', applyLayout);
      applyLayout();
    });
  }

  // ---------- Money formatter (basic; respects Shopify.formatMoney if present) ----------
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const value = (Number(cents) / 100).toFixed(2);
    return `₹${value}`;
  }

  // ---------- Boot ----------
  function init() {
    $$('[data-section-type="main-product"]').forEach((root) => {
      initVariantSelectors(root);
      initQuantityStepper(root);
      initAddToCart(root);
      initGallery(root);
      initStickyAtc(root);
      initShare(root);
    });
    initProductCardCartActions(document);
    initCarousels(document);
    initProductCarousels(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
